import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Box,
  Button,
  Chip,
  Icon,
  InputField,
  Loader,
  TabContent,
  TabGroup,
  TabItem,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import {
  getSidePanelChildForEntitlements,
  getSidePanelDependentsForEntitlements,
  getSidePanelPrivilegesForEntitlements,
  getSidePanelSodForEntitlements,
} from '../../../../../redux/certdetails/action';
import { SIDE_PANEL_LOAD_MORE_BUTTON } from '../../../../../redux/certdetails/types';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  CAMPAIGNTYPE_SERVICEACCOUNT,
  REF_PROP,
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
} from '../../../../../utils/constant';
import { useInvertedLocalize } from '../../../../../utils/localization';
import {
  getMessageKey,
  getRiskMeterParamsForConfidence,
  getStateForPAA,
} from '../../../../../utils/utils';
import { TrustJustification } from '../../../../CertsDetailsList';
import RiskScore from '../../../../RiskScore';
import { invertedMessages } from '../../messages';
import {
  FieldSet,
  HLine,
  SimpleAccordion,
  SODItem,
} from '../../SidePanelCommon';
import {
  CertsSidePanelBodyError,
  CertsSidePanelBodyHistory,
  CertsSidePanelBodySummary,
} from '../CertsSidePanelBodyCommon';
import {
  DEFAULT_COUNT_ACCORDIANS_OPEN,
  MAX_RECORDS_TO_FETCH,
  sodRiskButtonColor,
  TAB_CHILDREN,
  TAB_DEPENDENT,
  TAB_HISTORY,
  TAB_PRIVILEGE,
  TAB_SOD,
  TAB_SUMMARY,
} from '../constant';
import DATA from '../mockData';

import './CertsSidePanelEntitlements.css';

function CertsSidePanelEntitlements({
  dataTestId,
  className,
  data,
  activeTab,
  setActiveTab,
  isLoadingHistory,
  retriggerApisForError,
  handleCloseSidePanel,
  tabGroupSidePanelRef,
}) {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { messages } = useSelector((state) => state?.user);
  const {
    core: { configMap, isTrustScoreEnabled, campaignType },
    global: { certKey, mediaQueryValue },
  } = useSelector((state) => state?.certifications?.details);
  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
  const l = useInvertedLocalize(invertedMessages);

  const classes = classnames('CertsSidePanelBody', className);

  const [showHistory, setShowHistory] = useState(false);
  const [isLoadingMoreData, setIsLoadingMoreData] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [viewMore, setViewMore] = useState(
    isEOCampaign
      ? {
          [TAB_CHILDREN]: { currentOffset: 0 },
          [TAB_DEPENDENT]: { currentOffset: 0 },
        }
      : {
          [TAB_PRIVILEGE]: { currentOffset: 0 },
          [TAB_SOD]: { currentOffset: 0 },
          [TAB_CHILDREN]: { currentOffset: 0 },
          [TAB_DEPENDENT]: { currentOffset: 0 },
        }
  );
  const [childrenSearchForEnts, setChildrenSearchForEnts] = useState('');
  const [dependentSearchForEnts, setDependentSearchForEnts] = useState('');
  const [entitlementMenu, setEntitlementMenu] = useState([]);

  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileWidth = mobileXlargeQuery.value <= mediaQueryValue;
  const isSACampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT;

  const callApiForLoadBtn = (tab) => {
    setIsLoadingMoreData(true);

    switch (tab) {
      case TAB_PRIVILEGE:
        dispatch(
          getSidePanelPrivilegesForEntitlements(
            data.CERT_ENTITLEMENT_VALUEKEY,
            data.CERT_ACCOUNTKEY,
            String(
              viewMore[TAB_PRIVILEGE].currentOffset + MAX_RECORDS_TO_FETCH
            ),
            String(MAX_RECORDS_TO_FETCH),
            SIDE_PANEL_LOAD_MORE_BUTTON
          )
        )
          .then(() => {
            setViewMore((prevViewMore) => ({
              ...prevViewMore,
              [tab]: {
                currentOffset:
                  prevViewMore[tab].currentOffset + MAX_RECORDS_TO_FETCH,
              },
            }));
            setIsLoadingMoreData(false);
          })
          .catch(() => {
            setIsLoadingMoreData(false);
          });

        break;
      case TAB_SOD:
        dispatch(
          getSidePanelSodForEntitlements(
            certKey,
            data.CERT_ENTITLEMENT_VALUEKEY,
            data.CERT_ACCOUNTKEY,
            String(viewMore[TAB_SOD].currentOffset + MAX_RECORDS_TO_FETCH),
            String(MAX_RECORDS_TO_FETCH),
            '',
            SIDE_PANEL_LOAD_MORE_BUTTON
          )
        )
          .then(() => {
            setViewMore((prevViewMore) => ({
              ...prevViewMore,
              [tab]: {
                currentOffset:
                  prevViewMore[tab].currentOffset + MAX_RECORDS_TO_FETCH,
              },
            }));
            setIsLoadingMoreData(false);
          })
          .catch(() => {
            setIsLoadingMoreData(false);
          });
        break;

      case TAB_CHILDREN:
        dispatch(
          getSidePanelChildForEntitlements(
            data.CERT_ENTITLEMENT_VALUEKEY,
            String(viewMore[TAB_CHILDREN].currentOffset + MAX_RECORDS_TO_FETCH),
            String(MAX_RECORDS_TO_FETCH),
            SIDE_PANEL_LOAD_MORE_BUTTON
          )
        )
          .then(() => {
            setViewMore((prevViewMore) => ({
              ...prevViewMore,
              [tab]: {
                currentOffset:
                  prevViewMore[tab].currentOffset + MAX_RECORDS_TO_FETCH,
              },
            }));
            setIsLoadingMoreData(false);
          })
          .catch(() => {
            setIsLoadingMoreData(false);
          });
        break;

      case TAB_DEPENDENT:
        dispatch(
          getSidePanelDependentsForEntitlements(
            data.CERT_ENTITLEMENT_VALUEKEY,
            String(
              viewMore[TAB_DEPENDENT].currentOffset + MAX_RECORDS_TO_FETCH
            ),
            String(MAX_RECORDS_TO_FETCH),
            SIDE_PANEL_LOAD_MORE_BUTTON
          )
        )
          .then(() => {
            setViewMore((prevViewMore) => ({
              ...prevViewMore,
              [tab]: {
                currentOffset:
                  prevViewMore[tab].currentOffset + MAX_RECORDS_TO_FETCH,
              },
            }));
            setIsLoadingMoreData(false);
          })
          .catch(() => {
            setIsLoadingMoreData(false);
          });
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    setChildrenSearchForEnts('');
    setShowHistory(activeTab === TAB_HISTORY);
  }, [activeTab]);

  const handleTabChange = (value) => {
    setActiveTab(value);
  };

  const renderHistoryContent = () => {
    if (showHistory) {
      if (data?.sidePanel?.status[TAB_HISTORY]) {
        return (
          <TabContent
            className='CertsSidePanelBody-tabContent--history'
            value={activeTab}
            tabValue={TAB_HISTORY}>
            <CertsSidePanelBodyHistory
              data={data}
              isLoadingHistory={isLoadingHistory}
              isMobileWidth={isMobileWidth}
            />
          </TabContent>
        );
      }

      return (
        <CertsSidePanelBodyError
          tab={TAB_HISTORY}
          retriggerApisForError={retriggerApisForError}
          handleCloseSidePanel={handleCloseSidePanel}
        />
      );
    }

    return (
      <TabContent
        className='CertsSidePanelBody-tabContent--history'
        value={activeTab}
        tabValue={TAB_HISTORY}>
        <div />
      </TabContent>
    );
  };

  const renderSodTab = () => {
    if (data) {
      if (data?.sidePanel?.status[TAB_SOD]) {
        if (data?.sidePanel?.sod?.totalRisks > 0) {
          return (
            <TabItem
              label={l('Conflicts')}
              key={TAB_SOD}
              value={TAB_SOD}
              icon={
                <Icon kind='Entitlement' size='medium' color='neutral-1000' />
              }
              size='medium'
            />
          );
        }

        return (
          <span
            className='CertsSidePanelBody-tabGroupEmpty'
            key={TAB_SOD}
            value={TAB_SOD}
          />
        );
      }

      return (
        <TabItem
          label={l('Conflicts')}
          key={TAB_SOD}
          value={TAB_SOD}
          icon={<Icon kind='Entitlement' size='medium' color='neutral-1000' />}
          size='medium'
        />
      );
    }

    return (
      <span
        className='CertsSidePanelBody-tabGroupEmpty'
        key={TAB_SOD}
        value={TAB_SOD}
      />
    );
  };

  const renderPrivilegesTab = () => {
    if (data) {
      if (data?.sidePanel?.status[TAB_PRIVILEGE]) {
        if (data?.sidePanel?.privileges?.totalPrivileges > 0) {
          return (
            <TabItem
              label={l('Privilege')}
              key={TAB_PRIVILEGE}
              value={TAB_PRIVILEGE}
              icon={
                <Icon
                  kind='PrivilgedAccess'
                  size='medium'
                  color='neutral-1000'
                />
              }
              size='medium'
            />
          );
        }

        return (
          <span
            className='CertsSidePanelBody-tabGroupEmpty'
            key={TAB_PRIVILEGE}
            value={TAB_PRIVILEGE}
          />
        );
      }

      return (
        <TabItem
          label={l('Privilege')}
          key={TAB_PRIVILEGE}
          value={TAB_PRIVILEGE}
          icon={
            <Icon kind='PrivilgedAccess' size='medium' color='neutral-1000' />
          }
          size='medium'
        />
      );
    }

    return (
      <span
        className='CertsSidePanelBody-tabGroupEmpty'
        key={TAB_PRIVILEGE}
        value={TAB_PRIVILEGE}
      />
    );
  };

  const renderChildrenTabForEnts = () => {
    if (data) {
      if (data?.sidePanel?.status[TAB_CHILDREN]) {
        if (data?.sidePanel?.children?.totalChildEnt > 0) {
          return (
            <TabItem
              label={l('Children')}
              key={TAB_CHILDREN}
              value={TAB_CHILDREN}
              icon={
                <Icon kind='Entitlement' size='medium' color='neutral-1000' />
              }
              size='medium'
            />
          );
        }

        return (
          <span
            className='CertsSidePanelBody-tabGroupEmpty'
            key={TAB_CHILDREN}
            value={TAB_CHILDREN}
          />
        );
      }

      return (
        <TabItem
          label={l('Children')}
          key={TAB_CHILDREN}
          value={TAB_CHILDREN}
          icon={<Icon kind='Entitlement' size='medium' color='neutral-1000' />}
          size='medium'
        />
      );
    }

    return (
      <span
        className='CertsSidePanelBody-tabGroupEmpty'
        key={TAB_CHILDREN}
        value={TAB_CHILDREN}
      />
    );
  };

  const renderDependentsTabForEnts = () => {
    if (data) {
      if (data?.sidePanel?.status[TAB_DEPENDENT]) {
        if (data?.sidePanel?.dependents?.totalMappedEnt > 0) {
          return (
            <TabItem
              label={l('Dependents')}
              key={TAB_DEPENDENT}
              value={TAB_DEPENDENT}
              icon={
                <Icon kind='Entitlement' size='medium' color='neutral-1000' />
              }
              size='medium'
            />
          );
        }

        return (
          <span
            className='CertsSidePanelBody-tabGroupEmpty'
            key={TAB_DEPENDENT}
            value={TAB_DEPENDENT}
          />
        );
      }

      return (
        <TabItem
          label={l('Dependents')}
          key={TAB_DEPENDENT}
          value={TAB_DEPENDENT}
          icon={<Icon kind='Entitlement' size='medium' color='neutral-1000' />}
          size='medium'
        />
      );
    }

    return (
      <span
        className='CertsSidePanelBody-tabGroupEmpty'
        key={TAB_CHILDREN}
        value={TAB_CHILDREN}
      />
    );
  };

  const entValidTabsForMenu = () =>
    data?.sidePanel?.unFixedTabs?.filter((tab) => {
      if (tab === TAB_SOD) {
        if (
          data?.sidePanel?.status[TAB_SOD] &&
          data?.sidePanel?.sod?.totalRisks > 0
        )
          return true;
        if (!data?.sidePanel?.status[TAB_SOD]) return true;
      }

      if (tab === TAB_PRIVILEGE) {
        if (
          data?.sidePanel?.status[TAB_PRIVILEGE] &&
          data?.sidePanel?.privileges?.totalPrivileges > 0
        )
          return true;
        if (!data?.sidePanel?.status[TAB_PRIVILEGE]) return true;
      }

      if (tab === TAB_CHILDREN) {
        if (
          data?.sidePanel?.status[TAB_CHILDREN] &&
          data?.sidePanel?.children?.totalChildEnt > 0
        )
          return true;
        if (!data?.sidePanel?.status[TAB_CHILDREN]) return true;
      }

      if (tab === TAB_DEPENDENT) {
        if (
          data?.sidePanel?.status[TAB_DEPENDENT] &&
          data?.sidePanel?.dependents?.totalMappedEnt > 0
        )
          return true;
        if (!data?.sidePanel?.status[TAB_DEPENDENT]) return true;
      }

      return false;
    });

  const renderMenuForEntitlement = () => {
    const tempMenuList = [];

    if (data) {
      if (!isEOCampaign && entitlementMenu.includes(TAB_SOD)) {
        tempMenuList.push(renderSodTab());
      }

      if (!isEOCampaign && entitlementMenu.includes(TAB_PRIVILEGE)) {
        tempMenuList.push(renderPrivilegesTab());
      }

      if (entitlementMenu.includes(TAB_CHILDREN)) {
        tempMenuList.push(renderChildrenTabForEnts());
      }

      if (entitlementMenu.includes(TAB_DEPENDENT)) {
        tempMenuList.push(renderDependentsTabForEnts());
      }

      return tempMenuList;
    }

    return null;
  };

  const renderSodContent = () => {
    if (data?.sidePanel?.status[TAB_SOD]) {
      if (data?.sidePanel?.sod?.totalRisks > 0) {
        return (
          <TabContent
            className='CertsSidePanelBody-tabContent--sod'
            value={activeTab}
            tabValue={TAB_SOD}>
            {data?.sidePanel?.sod?.risks?.map((risk, index) => (
              <SimpleAccordion
                key={risk.index}
                title={risk.riskcode}
                iconKind='SOD'
                kind='sod'
                isInitialOpen={index < DEFAULT_COUNT_ACCORDIANS_OPEN}>
                <div
                  key={`${risk.index}-1`}
                  className='CertsSidePanelBody-tabContentSodRisk'>
                  <Typography
                    kind='body3'
                    className='CertsSidePanelBody-tabContentSodRiskText'>
                    {l('Risk Priority')}
                  </Typography>
                  <div className='CertsSidePanelBody-tabContentSodRiskButton'>
                    <Chip
                      size={isMobileWidth ? 'small' : 'medium'}
                      kind={
                        risk.risk_priority?.toLowerCase() === 'critical' ||
                        risk.risk_priority?.toLowerCase() === 'high'
                          ? 'boldFilled'
                          : 'subtleFilled'
                      }
                      label={risk.risk_priority || ''}
                      color={sodRiskButtonColor[risk.risk_priority] || ''}
                    />
                  </div>
                </div>
                {(risk.function1_name || risk.function2_name) && (
                  <div
                    key={`${risk.index}-2`}
                    className='CertsSidePanelBody-tabContentSodFuncGroup'>
                    <Typography
                      kind='body3'
                      className='CertsSidePanelBody-tabContentSodFuncGroupTitle'>
                      {l('Conflicting Job Functions')}
                    </Typography>
                    <div className='CertsSidePanelBody-tabContentSodFuncGroupBody'>
                      {risk.function1_name && (
                        <SODItem
                          key={`${risk.index}-2-1`}
                          sodItem={{
                            label: risk.function1_name,
                            description: risk.function1_description,
                          }}
                        />
                      )}
                      {risk.function2_name && (
                        <SODItem
                          key={`${risk.index}-2-2`}
                          sodItem={{
                            label: risk.function2_name,
                            description: risk.function2_description,
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}

                {index !== data.sidePanel.sod.risks.length - 1 && <HLine />}
              </SimpleAccordion>
            ))}
            {data?.sidePanel?.sod?.totalRisks > MAX_RECORDS_TO_FETCH && (
              <div className='CertsSidePanelBody-tabContentLoadBtn'>
                <Button
                  type='button'
                  kind='subtle'
                  size='medium'
                  isDisabled={
                    viewMore[TAB_SOD].currentOffset + MAX_RECORDS_TO_FETCH >
                      data?.sidePanel?.sod?.totalRisks || isLoadingMoreData
                  }
                  onClick={() => callApiForLoadBtn(TAB_SOD)}>
                  {l('Load More')}
                </Button>
              </div>
            )}
          </TabContent>
        );
      }

      return <span />;
    }

    return (
      <TabContent
        className='CertsSidePanelBody-tabContent'
        value={activeTab}
        tabValue={TAB_SOD}>
        <CertsSidePanelBodyError
          tab={TAB_SOD}
          retriggerApisForError={retriggerApisForError}
          handleCloseSidePanel={handleCloseSidePanel}
        />
      </TabContent>
    );
  };

  const renderPrivilegesContent = () => {
    if (data?.sidePanel?.status[TAB_PRIVILEGE]) {
      if (data?.sidePanel?.privileges?.totalPrivileges > 0) {
        return (
          <TabContent
            className='CertsSidePanelBody-tabContent-privileges'
            value={activeTab}
            tabValue={TAB_PRIVILEGE}>
            {data?.sidePanel?.privileges?.privileges?.map(
              (privilege, index) => (
                <>
                  <SimpleAccordion
                    key={privilege.attributeName}
                    title={privilege.attributeName}
                    iconKind='PrivilgedAccess'
                    pad='privileges'
                    isInitialOpen={!index}>
                    <ul className='CertsSidePanelBody-tabContentPrivilegesList'>
                      {privilege.attributeValue.split(',').map((value) => (
                        <li
                          key={`${privilege.attributeName}_${value}`}
                          className='CertsSidePanelBody-tabContentPrivilegesListItem'>
                          {`${value}`}
                        </li>
                      ))}
                    </ul>
                  </SimpleAccordion>
                  {index !==
                    data.sidePanel.privileges.privileges.length - 1 && (
                    <HLine />
                  )}
                </>
              )
            )}

            {data?.sidePanel?.privileges?.totalPrivileges >
              MAX_RECORDS_TO_FETCH && (
              <div className='CertsSidePanelBody-tabContentLoadBtn'>
                <Button
                  type='button'
                  kind='subtle'
                  size='medium'
                  isDisabled={
                    viewMore[TAB_PRIVILEGE].currentOffset +
                      MAX_RECORDS_TO_FETCH >
                      data?.sidePanel?.privileges?.totalPrivileges ||
                    isLoadingMoreData
                  }
                  onClick={() => callApiForLoadBtn(TAB_PRIVILEGE)}>
                  {l('Load More')}
                </Button>
              </div>
            )}
          </TabContent>
        );
      }

      return <span />;
    }

    return (
      <TabContent
        className='CertsSidePanelBody-tabContent'
        value={activeTab}
        tabValue={TAB_PRIVILEGE}>
        <CertsSidePanelBodyError
          tab={TAB_PRIVILEGE}
          retriggerApisForError={retriggerApisForError}
          handleCloseSidePanel={handleCloseSidePanel}
        />
      </TabContent>
    );
  };

  const renderChildrenContentForEntitlements = () => {
    if (data?.sidePanel?.status[TAB_CHILDREN]) {
      if (data?.sidePanel?.children?.totalChildEnt > 0) {
        const messageKeys = data?.sidePanel?.children?.messageKeys;

        return (
          <TabContent
            className='CertsSidePanelBody-tabContent--child'
            value={activeTab}
            tabValue={TAB_CHILDREN}>
            <div className='CertsSidePanelBody-tabContentChildDescription'>
              <Typography kind='body3'>
                {l(
                  "Child entitlements are subsets of access and permissions that correlate to a parent entitlement. These aren't always revoked when a parent entitlement is revoked."
                )}
              </Typography>
            </div>
            <InputField
              name='basic'
              kind='search'
              placeholder='Search by Entitlement Name'
              prefixIcon={
                <Icon kind='search' color='neutral-600' size='smallMedium' />
              }
              value={childrenSearchForEnts}
              setValue={setChildrenSearchForEnts}
            />
            {data?.sidePanel?.children?.childEntList
              .filter(
                (child) =>
                  `${child?.entitlement_value}`
                    .toLowerCase()
                    .indexOf(childrenSearchForEnts.toLowerCase()) !== -1
              )
              .map((child, index) => (
                <SimpleAccordion
                  key={child.index}
                  title={child.entitlement_value}
                  iconKind='Entitlement'
                  isInitialOpen={index < DEFAULT_COUNT_ACCORDIANS_OPEN}>
                  {Object.keys(child)
                    .filter(
                      (node) => node !== 'entitlement_value' && node !== 'index'
                    )
                    .map((node) => (
                      <FieldSet
                        key={`${child.index}-${node}`}
                        fieldData={{
                          label:
                            messages[messageKeys[node]] ||
                            localize(intl, getMessageKey(node)),
                          value: child?.[node],
                        }}
                      />
                    ))}
                  {index !==
                    data.sidePanel.children.childEntList.length - 1 && (
                    <HLine />
                  )}
                </SimpleAccordion>
              ))}
            {data?.sidePanel?.children?.totalChildEnt >
              MAX_RECORDS_TO_FETCH && (
              <div className='CertsSidePanelBody-tabContentLoadBtn'>
                <Button
                  type='button'
                  kind='subtle'
                  size='medium'
                  isDisabled={
                    viewMore[TAB_CHILDREN].currentOffset +
                      MAX_RECORDS_TO_FETCH >
                      data?.sidePanel?.children?.totalChildEnt ||
                    isLoadingMoreData
                  }
                  onClick={() => callApiForLoadBtn(TAB_CHILDREN)}>
                  {l('Load More')}
                </Button>
              </div>
            )}
          </TabContent>
        );
      }

      return <span />;
    }

    return (
      <TabContent
        className='CertsSidePanelBody-tabContent'
        value={activeTab}
        tabValue={TAB_CHILDREN}>
        <CertsSidePanelBodyError
          tab={TAB_CHILDREN}
          retriggerApisForError={retriggerApisForError}
          handleCloseSidePanel={handleCloseSidePanel}
        />
      </TabContent>
    );
  };

  const renderDependentsContentForEntitlements = () => {
    if (data?.sidePanel?.status[TAB_DEPENDENT]) {
      if (data?.sidePanel?.dependents?.totalMappedEnt > 0) {
        const messageKeys = data?.sidePanel?.dependents?.messageKeys;

        return (
          <TabContent
            className='CertsSidePanelBody-tabContent--child'
            value={activeTab}
            tabValue={TAB_DEPENDENT}>
            <div className='CertsSidePanelBody-tabContentChildDescription'>
              <Typography kind='body3'>
                {l(
                  'Dependent entitlements are subsets of access and permissions that are directly mapped to a parent entitlement. If the parent entitlement is revoked, these are also revoked.'
                )}
              </Typography>
            </div>
            <InputField
              name='basic'
              kind='search'
              placeholder='Search by Entitlement Name'
              prefixIcon={
                <Icon kind='search' color='neutral-600' size='smallMedium' />
              }
              value={dependentSearchForEnts}
              setValue={setDependentSearchForEnts}
            />

            {data?.sidePanel?.dependents?.mappedEntList
              ?.filter(
                (dependent) =>
                  `${dependent?.permission}`
                    .toLowerCase()
                    .indexOf(dependentSearchForEnts.toLowerCase()) !== -1
              )
              ?.map((dependent, index) => (
                <SimpleAccordion
                  key={dependent?.index}
                  title={dependent?.permission}
                  iconKind='Entitlement'
                  isInitialOpen={index < DEFAULT_COUNT_ACCORDIANS_OPEN}>
                  {Object.keys(dependent)
                    .filter((node) => node !== 'permission' && node !== 'index')
                    .map((node) => (
                      <FieldSet
                        key={`${dependent?.index}-${node}`}
                        fieldData={{
                          label:
                            messages[messageKeys[node]] ||
                            localize(intl, getMessageKey(node)),
                          value: dependent?.[node],
                        }}
                      />
                    ))}
                  {index !==
                    data.sidePanel.dependents.mappedEntList.length - 1 && (
                    <HLine />
                  )}
                </SimpleAccordion>
              ))}
            {data?.sidePanel?.dependents?.totalMappedEnt >
              MAX_RECORDS_TO_FETCH && (
              <div className='CertsSidePanelBody-tabContentLoadBtn'>
                <Button
                  type='button'
                  kind='subtle'
                  size='medium'
                  isDisabled={
                    viewMore[TAB_DEPENDENT].currentOffset +
                      MAX_RECORDS_TO_FETCH >
                      data?.sidePanel?.dependents?.totalMappedEnt ||
                    isLoadingMoreData
                  }
                  onClick={() => callApiForLoadBtn(TAB_DEPENDENT)}>
                  {l('Load More')}
                </Button>
              </div>
            )}
          </TabContent>
        );
      }

      return <span />;
    }

    return (
      <TabContent
        className='CertsSidePanelBody-tabContent'
        value={activeTab}
        tabValue={TAB_DEPENDENT}>
        <CertsSidePanelBodyError
          tab={TAB_DEPENDENT}
          retriggerApisForError={retriggerApisForError}
          handleCloseSidePanel={handleCloseSidePanel}
        />
      </TabContent>
    );
  };

  useEffect(() => {
    if (data) {
      if (data?.sidePanel?.status[TAB_SUMMARY]) setIsLoadingData(false);

      setEntitlementMenu(entValidTabsForMenu());
    }
  }, [data]);

  const shouldShowSidePanelSummaryForEntitlement = Boolean(
    data?.sidePanel?.summary && data?.sidePanel?.status[TAB_SUMMARY]
  );

  const renderScoreMeter = () => {
    if (isTrustScoreEnabled) {
      // Check if score coming from confidence
      if (isTrustScoreEnabled?.enableCertificationTrustModel) {
        const upperLimitValue =
          configMap?.TRUST_LIKELYAPPROVAL_LOWER?.trim()?.length !== 0
            ? Number(configMap?.TRUST_LIKELYAPPROVAL_LOWER)
            : null;
        const lowerLimitValue =
          configMap?.TRUST_POSSIBLEREJECT_UPPER?.trim()?.length !== 0
            ? Number(configMap?.TRUST_POSSIBLEREJECT_UPPER)
            : null;
        const middleRange = String(configMap?.TRUST_POSSIBLEAPPROVAL);

        const {
          score: riskScore,
          kind: riskKind,
          label: riskLabel,
        } = getRiskMeterParamsForConfidence(
          Number(data?.TRUST_SCORE) || Number(data?.JRMRULES?.TRUST_SCORE),
          upperLimitValue,
          lowerLimitValue,
          middleRange
        );

        if (riskKind === 'info') return null;

        return (
          <>
            <div className='CertsSidePanelBody-tabContentSummaryShorts'>
              <div className='.CertsSidePanelBody-tabContentSummaryShortsStatus'>
                <Typography kind='h4'>{l('Trust')}</Typography>
              </div>
              <div
                className={
                  isMobileWidth
                    ? 'CertsSidePanelBody-tabContentSummaryShortsRisk--mobileResponsive'
                    : 'CertsSidePanelBody-tabContentSummaryShortsRisk'
                }>
                <RiskScore
                  kind={riskKind}
                  size={isMobileWidth ? 'small' : 'regular'}
                  value={riskScore}
                  label={riskLabel}
                  shouldShowZero
                />
                <div className='CertsSidePanelBody-tabContentSummaryShortsRiskList'>
                  <TrustJustification
                    isTrustScoreEnabled={isTrustScoreEnabled}
                    nodeData={data}
                    certsKind='Entitlement'
                  />
                </div>
              </div>
            </div>
            <HLine />
          </>
        );
      }

      const riskScore = Number(data?.JRMRULES?.cnf || '0');
      const { kind: riskKind, label: riskLabel } = getStateForPAA(
        data?.JRMRULES?.rmd
      );

      if (riskKind === 'info' || Number(riskScore) === 0) return null;

      return (
        <>
          <div className='CertsSidePanelBody-tabContentSummaryShorts'>
            <div className='.CertsSidePanelBody-tabContentSummaryShortsStatus'>
              <Typography kind='h4'>{l('Trust')}</Typography>
            </div>
            <div
              className={
                isMobileWidth
                  ? 'CertsSidePanelBody-tabContentSummaryShortsRisk--mobileResponsive'
                  : 'CertsSidePanelBody-tabContentSummaryShortsRisk'
              }>
              <RiskScore
                kind={riskKind}
                size={isMobileWidth ? 'small' : 'regular'}
                value={riskScore}
                label={riskLabel}
              />
              <div className='CertsSidePanelBody-tabContentSummaryShortsRiskList'>
                <TrustJustification
                  isTrustScoreEnabled={isTrustScoreEnabled}
                  nodeData={data}
                  certsKind='Entitlement'
                />
              </div>
            </div>
          </div>
          <HLine />
        </>
      );
    }

    return null;
  };

  const renderSummaryContent = () => {
    if (!isLoadingData && shouldShowSidePanelSummaryForEntitlement) {
      return <CertsSidePanelBodySummary data={data} />;
    }

    if (
      !isLoadingData &&
      !shouldShowSidePanelSummaryForEntitlement &&
      !data?.sidePanel?.status[TAB_SUMMARY]
    ) {
      return (
        <CertsSidePanelBodyError
          tab={TAB_SUMMARY}
          retriggerApisForError={retriggerApisForError}
          handleCloseSidePanel={handleCloseSidePanel}
        />
      );
    }

    return (
      <Box className='SidePanelView-loader'>
        <Loader kind='dots' format='inline' color='info' size='medium' />
      </Box>
    );
  };

  return (
    <section className={classes} data-testid={dataTestId}>
      <TabGroup
        moreContainerRef={tabGroupSidePanelRef}
        value={activeTab}
        isVerticalWithOverflow={isMobileWidth}
        onChange={handleTabChange}
        isTabOrderBeFixed
        className='CertsSidePanelBody-tabGroup'>
        <TabItem
          label={l('Summary')}
          key={TAB_SUMMARY}
          value={TAB_SUMMARY}
          icon={<Icon kind='Document' size='medium' color='neutral-1000' />}
          size='medium'
        />
        <TabItem
          label={l('History')}
          key={TAB_HISTORY}
          value={TAB_HISTORY}
          icon={
            <Icon kind='RequestHistory' size='medium' color='neutral-1000' />
          }
          size='medium'
        />
        {renderMenuForEntitlement()}
        <span
          className='CertsSidePanelBody-tabGroupEmpty'
          key={TAB_SOD}
          value={TAB_SOD}
        />
      </TabGroup>

      <TabContent
        className='CertsSidePanelBody-tabContent--summary'
        value={activeTab}
        tabValue={TAB_SUMMARY}>
        {isTrustScoreEnabled &&
          shouldShowSidePanelSummaryForEntitlement &&
          !isSACampaign &&
          renderScoreMeter()}

        {renderSummaryContent()}
      </TabContent>

      {renderHistoryContent()}
      {renderSodContent()}
      {renderPrivilegesContent()}
      {renderChildrenContentForEntitlements()}
      {renderDependentsContentForEntitlements()}
    </section>
  );
}

CertsSidePanelEntitlements.propTypes = {
  dataTestId: PropTypes.string,
  className: PropTypes.string,
  data: PropTypes.shape(DATA).isRequired,
  activeTab: PropTypes.string.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  isLoadingHistory: PropTypes.bool.isRequired,
  retriggerApisForError: PropTypes.func,
  handleCloseSidePanel: PropTypes.func,
  tabGroupSidePanelRef: REF_PROP.isRequired,
};

CertsSidePanelEntitlements.defaultProps = {
  dataTestId: null,
  className: null,
  retriggerApisForError: () => {},
  handleCloseSidePanel: () => {},
};

export default CertsSidePanelEntitlements;
